import React, { useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import api from "../api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface MonthlyStatsData {
  meta: {
    month: number;
    year: number;
    month_name: string;
  };
  appointments: {
    total: number;
    by_status: {
      confirmed: number;
      pending: number;
      cancelled: number;
      completed: number;
    };
    daily_trends: Array<{ date: string; count: number }>;
    peak_hours: Array<{ hour: number; count: number }>;
  };
  patients: {
    total: number;
    registration_trends: Array<{ date: string; count: number }>;
    monthly_growth: Array<{ month: string; count: number }>;
  };
}

const MonthlyStats: React.FC = () => {
  const [currentDate, setCurrentDate] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<MonthlyStatsData>({
    meta: { month: 0, year: 0, month_name: "" },
    appointments: {
      total: 0,
      by_status: { confirmed: 0, pending: 0, cancelled: 0, completed: 0 },
      daily_trends: [],
      peak_hours: [],
    },
    patients: {
      total: 0,
      registration_trends: [],
      monthly_growth: [],
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  useEffect(() => {
    document.title = "Estadísticas mensuales";
    const fetchMonthlyStats = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.get<MonthlyStatsData>(
          "/appointments/monthly-stats/",
          {
            params: {
              month: currentDate.month,
              year: currentDate.year,
            },
          }
        );
        setData(response.data);
      } catch (err) {
        setError("Error al cargar los datos. Verifica los parámetros.");
      } finally {
        setLoading(false);
      }
    };
    fetchMonthlyStats();
  }, [currentDate.month, currentDate.year]);

  const handleDateChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setCurrentDate((prev) => ({
      ...prev,
      [name]: parseInt(value),
    }));
  };

  // Configuraciones de gráficos
  const statusChartData = {
    labels: ["Confirmadas", "Pendientes", "Canceladas", "Completadas"],
    datasets: [
      {
        label: "Citas por estado",
        data: [
          data.appointments.by_status.confirmed,
          data.appointments.by_status.pending,
          data.appointments.by_status.cancelled,
          data.appointments.by_status.completed,
        ],
        backgroundColor: [
          "rgba(75, 192, 192, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
        ],
        borderColor: [
          "rgba(75, 192, 192, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const dailyTrendsData = {
    labels: data.appointments.daily_trends.map((t) =>
      new Date(t.date).toLocaleDateString("es-ES")
    ),
    datasets: [
      {
        label: "Citas por día",
        data: data.appointments.daily_trends.map((t) => t.count),
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.1,
        fill: true,
      },
    ],
  };

  const formatHour = (hour: number) => {
    return new Date(2000, 0, 1, hour).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const peakHoursData = {
    labels: data.appointments.peak_hours.map((h) => formatHour(h.hour)),
    datasets: [
      {
        label: "Citas por hora",
        data: data.appointments.peak_hours.map((h) => h.count),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        tension: 0.1,
        fill: true,
      },
    ],
  };

  const registrationTrendsData = {
    labels: data.patients.registration_trends.map((t) =>
      new Date(t.date).toLocaleDateString("es-ES")
    ),
    datasets: [
      {
        label: "Registros diarios",
        data: data.patients.registration_trends.map((t) => t.count),
        borderColor: "rgb(156, 39, 176)",
        backgroundColor: "rgba(156, 39, 176, 0.2)",
        tension: 0.1,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        color: "#374151",
        font: { size: 16 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  // if (loading)
  //   return <div className="text-center py-4">Cargando estadísticas...</div>;
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Cargando estadísticas...</p>
      </div>
    );
  }
  if (error)
    return <div className="text-center py-4 text-red-600">{error}</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="date-picker-container">
        <select
          name="month"
          value={currentDate.month}
          onChange={handleDateChange}
          className="date-select"
        >
          {months.map((month, index) => (
            <option key={month} value={index + 1}>
              {month}
            </option>
          ))}
        </select>

        <input
          type="number"
          name="year"
          value={currentDate.year}
          onChange={handleDateChange}
          className="date-input"
          min="2000"
          max="2100"
        />

        <div className="current-date-display">
          {data.meta.month_name} {data.meta.year}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="summary-container">
          <h2 className="section-title-stats">Resumen general</h2>
          <div className="grid-container">
            {/* Total Patients */}
            <div className="info-box">
              <i className="fas fa-user-injured grid-icon"></i>
              <div className="grid-content">
                <h3>PACIENTES</h3>
                <p>{data.patients.total}</p>
              </div>
            </div>

            {/* Completed Appointments */}
            <div className="info-box">
              <i className="fas fa-calendar-check grid-icon"></i>
              <div className="grid-content">
                <h3>CITAS COMPLETADAS</h3>
                <p>{data.appointments.by_status.completed}</p>
              </div>
            </div>

            {/* Pending Appointments */}
            <div className="info-box">
              <i className="fas fa-calendar-day grid-icon"></i>
              <div className="grid-content">
                <h3>CITAS PENDIENTES</h3>
                <p className="highlight">
                  {data.appointments.by_status.pending}
                </p>
              </div>
            </div>

            {/* Monthly Records */}
            <div className="info-box">
              <i className="fas fa-file-alt grid-icon"></i>
              <div className="grid-content">
                <h3>REGISTROS ESTE MES</h3>
                <p>{data.patients.total}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="section-subtitle-stats">Distribución de citas</h3>
          <div className="h-64">
            <Bar data={statusChartData} options={chartOptions} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="section-subtitle-stats">Horas Pico</h3>
          <div className="h-64">
            <Line data={peakHoursData} options={chartOptions} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="section-subtitle-stats">Citas Diarias</h3>
          <div className="h-64">
            <Line data={dailyTrendsData} options={chartOptions} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="section-subtitle-stats">
            Registro diario de pacientes
          </h3>
          <div className="h-64">
            <Line data={registrationTrendsData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyStats;
